import { notify } from '@ps-int/ps-components/src/notifications/slice';
import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, select } from 'typed-redux-saga';

import { NOTIFICATIONS_MAP } from '../../../../../shared/features/notifications/ps-notifications';
import { toLoggableError } from '../../../../../shared/helpers/errors';
import { ACTION, FEATURE } from '../../../../../shared/helpers/logger-enums';
import { countMetrika, MetrikaParam } from '../../../../../shared/helpers/metrika';
import { cloudApi } from '../../../../../shared/helpers/transport';
import { getConferenceConfig, selectWaitingRoomPeers } from '../../../selectors';
import { AdmitOneParams, NotificationId } from '../../../types';
import { logger } from '../helpers/logger';
import { setHasNewWaitingRoomPeers, setIsAdmitting } from '../slice';

export function* admitWaitingRoomPeerSaga({ payload: admitParams }: PayloadAction<AdmitOneParams>) {
    const { peerId } = admitParams ?? {};
    let admitSucceeded = false;

    try {
        yield* call(countAdmitOneMetrika, admitParams, 'press');
        const conferenceConfig = yield* select(getConferenceConfig);
        if (!conferenceConfig || !peerId) {
            return;
        }
        yield* put(setIsAdmitting({ id: peerId, value: true }));
        yield* call([cloudApi, cloudApi.waitingRoomAdmit], conferenceConfig.resolved_uri, peerId);

        const peers = yield* select(selectWaitingRoomPeers);
        const currentPeersCount = peers.filter((peer) => peer.id !== peerId).length;
        if (currentPeersCount <= 0) {
            yield* put(setHasNewWaitingRoomPeers(false));
        }

        yield* call(countAdmitOneMetrika, admitParams, 'success');

        admitSucceeded = true;
    } catch (error) {
        const loggableError = toLoggableError(error, {
            method: 'admitWaitingRoomPeerSaga',
        });

        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.WAITING_ROOM_ADMIT_ERROR]()));
        yield* call(countAdmitOneMetrika, admitParams, 'fail', loggableError.name, loggableError.message);
        logger.error(loggableError);
    } finally {
        if (peerId) {
            logger.endFeatureDelta({
                feature: FEATURE.WAITING_ROOM,
                action: ACTION.ALLOW_PARTICIPANT,
                isSuccess: admitSucceeded,
                id: peerId,
            });

            yield* put(setIsAdmitting({ id: peerId, value: false }));
        }
    }
}

const admitAuthorisedOneFromToastMetrika = {
    press: ['waiting_rooms', 'waiting_list', 'authorised_waiting_come_in_press'],
    success: ['waiting_rooms', 'waiting_list', 'authorised_waiting_come_in_success'],
    fail: ['waiting_rooms', 'waiting_list', 'authorised_waiting_come_in_fail'],
};
const admitOneFromToastMetrika = {
    press: ['waiting_rooms', 'waiting_list', 'guest_waiting_come_in_press'],
    success: ['waiting_rooms', 'waiting_list', 'guest_waiting_come_in_success'],
    fail: ['waiting_rooms', 'waiting_list', 'guest_waiting_come_in_fail'],
};
const admitOneFromListMetrika = {
    press: ['waiting_rooms', 'waiting_list', 'let_user_come_in_press'],
    success: ['waiting_rooms', 'waiting_list', 'let_user_come_in_success'],
    fail: ['waiting_rooms', 'waiting_list', 'let_user_come_in_fail'],
};

function* countAdmitOneMetrika(
    admitParams: AdmitOneParams,
    type: 'press' | 'success' | 'fail',
    ...args: MetrikaParam[]
) {
    try {
        const { uid, fromToast } = admitParams ?? {};
        const isAuthorised = Boolean(uid);
        const metrika = fromToast
            ? isAuthorised
                ? admitAuthorisedOneFromToastMetrika
                : admitOneFromToastMetrika
            : admitOneFromListMetrika;
        const action = metrika[type];
        yield* call(countMetrika, ...action, ...(args ?? []));
    } catch (error) {
        logger.error(
            toLoggableError(error, {
                method: 'countAdmitOneMetrika',
            })
        );
    }
}
